import { ClerkProvider } from '@clerk/nextjs';
import { CacheProvider, EmotionCache } from '@emotion/react';
import { NoSsr } from '@mui/material';
import { HydrationBoundary, QueryClientProvider } from '@tanstack/react-query';
import type { NextPage } from 'next';
import type { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { ReactElement, ReactNode, useMemo, useState } from 'react';
import { IntlProvider } from 'react-intl';

import { EmbedConfig, parseEmbedParams } from 'utils/embed.utils';
import { selectEmotionCache } from 'utils/emotion-cache.utils';
import { isLocal, isProd, isStaging } from 'utils/env.utils';

import { INTL_LOCALE_MAP } from 'constants/i18n.constants';
import { useLocale } from 'hooks/useLocale';
import { useReportScamSite } from 'hooks/useReportScamSite';
import { useTrackAffiliateSource } from 'hooks/useTrackAffiliateSource';
import { AvoEnv, Environment, gtmDestination, initAvo } from 'services/avo';
import { AvoInspector } from 'services/avo/inspector';
import { ConsentManager, CookieConsentUI } from 'services/consent-manager';
import { messages } from 'services/intl';
import LazyMotion from 'services/motion';
import { createQueryClient } from 'services/react-query';
import { SnackbarProvider } from 'services/snackbars';
import { NofomoStoreProvider } from 'services/stores/StoreProvider';
import ThemeContext from 'services/theme-context';

import OnboardedCheck from 'components/@account/OnboardedCheck';
import Layout from 'components/@screens/Layout';
import DefaultSeo from 'components/@seo/DefaultSeo';

const ReactQueryDevtools = dynamic(() =>
  import(
    /* webpackChunkName: 'ReactQueryDevTools' */ '@tanstack/react-query-devtools'
  ).then((c) => c.ReactQueryDevtools),
);

const mapToAvoEnvironment = (() => {
  if (isProd) return Environment.PROD;
  if (isStaging) return Environment.STAGING;
  if (isLocal) return Environment.DEV;
  return Environment.DEV;
})();

// Initialize Avo outside React land
initAvo(
  {
    env: isProd ? AvoEnv.Prod : AvoEnv.Dev,
    inspector: AvoInspector,
    webDebugger: process.env.NEXT_PUBLIC_ENABLE_AVO_DEBUGGER === 'true',
    reportFailureAs: 'error',
    avoLogger: {
      logDebug: () => false,
      logError: () => false,
      logWarn: () => false,
    },
  },
  {
    platformType: 'web',
    hostName: 'nofomo.com',
    environment: mapToAvoEnvironment,
  },
  {},
  gtmDestination,
);

type PageProps = {
  dehydratedState: unknown;
  embed?: EmbedConfig;
};

/**
 * A page can bring its own layout. It renders outside the page component, so
 * it survives navigation between pages that share it rather than remounting.
 */
export type NextPageWithLayout<P = object> = NextPage<P> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

interface MyAppProps extends AppProps {
  emotionCache?: EmotionCache;
  pageProps: PageProps;
  Component: NextPageWithLayout;
}

const App = (props: MyAppProps) => {
  useTrackAffiliateSource();
  useReportScamSite();
  const { locale, defaultLocale } = useLocale();
  const { isPreview, query } = useRouter();
  const {
    emotionCache = selectEmotionCache(locale),
    Component,
    pageProps,
  } = props;

  const getLayout = Component.getLayout ?? ((page: ReactElement) => page);

  const [queryClient] = useState(() =>
    createQueryClient({ preview: isPreview }),
  );

  const { dehydratedState, embed: embedProps, ...otherPageProps } = pageProps;

  // Pages that resolve it on the server are authoritative.
  const embed = useMemo(
    () => embedProps ?? parseEmbedParams(query),
    [embedProps, query],
  );

  return (
    <ConsentManager>
      <CacheProvider value={emotionCache}>
        <QueryClientProvider client={queryClient}>
          <HydrationBoundary state={dehydratedState}>
            <IntlProvider
              locale={INTL_LOCALE_MAP[locale]}
              defaultLocale={defaultLocale}
              messages={messages[locale]}
            >
              <ThemeContext locale={locale}>
                {embed.cookiesPopup && <CookieConsentUI />}
                <NofomoStoreProvider embed={embed}>
                  <LazyMotion>
                    <Layout>
                      <DefaultSeo />
                      <ClerkProvider
                        clerkJSVariant="headless"
                        telemetry={false}
                      >
                        <SnackbarProvider>
                          {getLayout(<Component {...otherPageProps} />)}
                          <NoSsr>
                            <OnboardedCheck />
                          </NoSsr>
                        </SnackbarProvider>
                      </ClerkProvider>
                    </Layout>
                  </LazyMotion>
                </NofomoStoreProvider>
              </ThemeContext>
            </IntlProvider>
          </HydrationBoundary>
          {isLocal && <ReactQueryDevtools buttonPosition="bottom-left" />}
        </QueryClientProvider>
      </CacheProvider>
    </ConsentManager>
  );
};

export default App;
